import React, { useState, useEffect } from 'react';
import backendAPI from '../api/backendApi';

const Dashboard = ({ user }) => {
  const [selectedWeek, setSelectedWeek] = useState(2);
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState({});
  const [mondayPoints, setMondayPoints] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [view, setView] = useState('picks');
  const [loading, setLoading] = useState(false);

  // Load games when week changes
  useEffect(() => {
    loadGames();
  }, [selectedWeek]);

  // Load leaderboard when switching to leaderboard view
  useEffect(() => {
    if (view === 'leaderboard') {
      loadLeaderboard();
    }
  }, [view, selectedWeek]);

  const loadGames = async () => {
    setLoading(true);
    try {
      const response = await backendAPI.getGames(selectedWeek, 2025);
      if (response.success) {
        setGames(response.games || []);
      }
    } catch (error) {
      console.error('Error loading games:', error);
    }
    setLoading(false);
  };

  const loadLeaderboard = async () => {
    try {
      const response = await backendAPI.getLeaderboard(selectedWeek, 'all', 2025);
      if (response.success) {
        setLeaderboard(response.leaderboard || []);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const handlePickChange = (gameId, team) => {
    setPicks(prev => ({
      ...prev,
      [gameId]: team
    }));
  };

  const handleSubmitPicks = async () => {
    if (selectedWeek === 1) {
      alert('Picks not allowed for Week 1');
      return;
    }

    if (Object.keys(picks).length === 0) {
      alert('Please make at least one pick');
      return;
    }

    if (!mondayPoints) {
      alert('Please enter Monday Night total points');
      return;
    }

    setLoading(true);
    try {
      const scorecardData = {
        userEmail: user.email,
        username: user.username,
        week: selectedWeek,
        year: 2025,
        selections: picks,
        mondayNightPoints: parseInt(mondayPoints),
        tier: 'playmaker',
        price: 25,
        paymentMethod: 'test'
      };

      const response = await backendAPI.submitScorecard(scorecardData);
      if (response.success) {
        alert('Picks submitted successfully!');
        setPicks({});
        setMondayPoints('');
      } else {
        alert('Error: ' + response.error);
      }
    } catch (error) {
      alert('Error submitting picks: ' + error.message);
    }
    setLoading(false);
  };

  return (
    <div className="dashboard">
      <h1>SportIQ - Welcome {user.firstName}!</h1>
      
      {/* Week Selector */}
      <div className="week-selector">
        <label>Select Week: </label>
        {[1, 2, 3, 4, 5].map(week => (
          <button 
            key={week}
            className={selectedWeek === week ? 'active' : ''}
            onClick={() => setSelectedWeek(week)}
          >
            Week {week}
          </button>
        ))}
      </div>

      {/* View Toggle */}
      <div className="view-toggle">
        <button 
          className={view === 'picks' ? 'active' : ''}
          onClick={() => setView('picks')}
        >
          Make Picks
        </button>
        <button 
          className={view === 'leaderboard' ? 'active' : ''}
          onClick={() => setView('leaderboard')}
        >
          Leaderboard
        </button>
      </div>

      {/* Picks View */}
      {view === 'picks' && (
        <div className="picks-section">
          <h2>Week {selectedWeek} Games</h2>
          
          {selectedWeek === 1 && (
            <p style={{color: 'orange', fontWeight: 'bold', textAlign: 'center', padding: '1rem'}}>
              Week 1 is view-only. Picks available for weeks 2-5.
            </p>
          )}

          {loading ? (
            <p>Loading games...</p>
          ) : (
            <div className="games-list">
              {games.map(game => (
                <div key={game.id} className="game-card">
                  <div className="game-info">
                    <span>{game.away_team} @ {game.home_team}</span>
                    <span>{new Date(game.game_date).toLocaleDateString()}</span>
                  </div>
                  
                  {selectedWeek > 1 && (
                    <div className="pick-buttons">
                      <button 
                        className={picks[game.id] === game.away_team ? 'selected' : ''}
                        onClick={() => handlePickChange(game.id, game.away_team)}
                      >
                        {game.away_team}
                      </button>
                      <button 
                        className={picks[game.id] === game.home_team ? 'selected' : ''}
                        onClick={() => handlePickChange(game.id, game.home_team)}
                      >
                        {game.home_team}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {selectedWeek > 1 && (
            <div className="monday-points">
              <label>
                Monday Night Total Points: 
                <input 
                  type="number" 
                  value={mondayPoints}
                  onChange={(e) => setMondayPoints(e.target.value)}
                  placeholder="e.g. 45"
                />
              </label>
              <button onClick={handleSubmitPicks} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Picks'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard View */}
      {view === 'leaderboard' && (
        <div className="leaderboard-section">
          <h2>Week {selectedWeek} Leaderboard</h2>
          
          {leaderboard.length === 0 ? (
            <p>No submissions yet for Week {selectedWeek}</p>
          ) : (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Username</th>
                  <th>Score</th>
                  <th>Correct Picks</th>
                  <th>Tier</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, index) => (
                  <tr key={entry.userEmail}>
                    <td>{index + 1}</td>
                    <td>{entry.username}</td>
                    <td>{entry.totalScore || 0}</td>
                    <td>{entry.correctPicks || 0}</td>
                    <td>{entry.tier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;